import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'awareness',
  slug: 'awareness',
  label: 'Awareness',
  roll: {
    formula: 'Per 1 + Awareness 2 = 3 + die',
    niche: '+1 when actively searching',
  },
  whenToUse: [
    'Spot inconsistencies in a manuscript or library catalog',
    'Detect an intruder or hostile presence in a familiar space',
    'Notice disturbed dust, misplaced texts, or signs of tampering',
    'Recognize an unfamiliar scent or sound that breaks routine',
  ],
  samples: [
    `GM: "The spring meadow near Ex Astra sprawls before the scouts in spring 1195. Elara scans the ridge while Sir Helgi tests the grass underfoot."
You (Mikaela): "I watch the skyline for any glint of metal or movement."
GM: "A glimmer—just for a moment. Sunlight catching steel on the ridge above the meadow. Someone with a weapon or spyglass is watching."
GM: "Roll Per 1 + Awareness 2 = 3 + die."

Teaches: Mikaela's Curious trait (+3) drives her to notice what others miss—vigilance is part of her nature.`,
    `GM: "Inside the ruined chapel, shadows pool in every corner. Corvus tests the floor's weight while Brother Alfric searches the altar. This is Ex Astra business in spring 1195."
You (Mikaela): "I move slow and steady, my eyes on the stone. Something important is hidden here."
GM: "Beneath three centuries of fallen plaster, Mikaela traces the faded chalk of a ritual circle. It was worked recently—the chalk hasn't fully weathered. This chapel was active within the year."
GM: "Roll Per 1 + Awareness 2 = 3 + die, +1 for niche actively searching."

Teaches: Mikaela's Curious trait (+3) compels her to examine what lies beneath the obvious.`,
    `GM: "The deer path winds through dense woodland. Boot-prints mark the soft earth. Sir Helgi kneels to examine them while the party holds its breath. Your Ex Astra group travels spring 1195."
You (Mikaela): "I check the area for other signs—broken branches, disturbed moss, anything else."
GM: "Half-buried in leaf-litter, Mikaela spots a second set of prints, deeper and heavier. Not a scout. A person carrying weight—supplies, armor, or both. These travelers passed within the last two days."
GM: "Roll Per 1 + Awareness 2 = 3 + die."

Teaches: Your Short of Breath flaw means you pace yourself, moving steady and slow—paradoxically perfect for detail work.`,
    `GM: "Night settles over the camp. The stars emerge. Then Mikaela freezes. This is Ex Astra covenant territory in spring 1195."
Brother Alfric: "What is it?"
You (Mikaela): "A twig snapped. Behind us. Not natural wind."
GM: "There. Thirty paces back through the brush. The faintest movement. Hoofprints and boot-scuff in the earth beyond the firelight. Someone—or something—approached while you slept, tested the camp's perimeter, and withdrew."
GM: "Roll Per 1 + Awareness 2 = 3 + die."

Teaches: Your Curious trait (+3) will not let danger slip past unnoticed.`,
    `GM: "The herb-merchant's stall overflows with bundles. Elara negotiates price while Brother Alfric asks questions about sourcing. This is the Not-Quite-Free Real Estate sidequest. Mikaela has three minutes. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
You (Mikaela): "I search the stall fast—every shelf, every corner. The sidequest demands one specific plant, and I will find it."
GM: "Beneath common vervain and dried rosemary, Mikaela spots jars arranged by rarity. The one she needs—nightshade—sits in the back row, carefully hidden but not locked. The merchant knows what he's selling. This ingredient is restricted."
GM: "Roll Per 1 + Awareness 2 = 3 + die, +1 for niche actively searching."

Teaches: Mikaela's Clerk virtue trains her eye for organized systems—even a merchant's hidden rarity fits a pattern she can read.`,
  ],
  vignette: 'Mikaela shelves returned codices with methodical precision, her fingers tracing spine after spine. She pauses. That volume on the third shelf—the one on Hermetic theory—sits fractionally askew from her careful arrangement. Someone has been in the archive.',
  gotcha: 'Folk Ken notices deception in people; Awareness notices absence or displacement. Concentration sustains Awareness over time; Awareness itself is a momentary check.',
};

export default demo;
